import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import {
  TradePackage,
  TradeCard as ApiTradeCard,
  SetDetail,
} from 'store/services/trade/tradeApi';
import { useWindowSize } from 'hooks/useWindowSize';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { Points } from 'components/Points/Points';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import {
  getSetsAcceptedSummary,
  getSetsAcceptedCount,
  getFinishSummary,
  getConditionSummary,
  getLanguageSummary,
  getCardDisplayPrice,
  calculateTotalValue,
} from './listViewUtils';
import { buildCardUrl } from 'utils/cardUrlUtils';
import './SendListView.css';

// Helper to clean malformed URLs from backend (same as SendPage2)
const cleanImageUrl = (url: string | null | undefined): string => {
  if (!url) return AssetUrls.profile.defaultAvatar;
  if (url.includes('{failed')) {
    const cleanUrl = url.split('{failed')[0].trim();
    return cleanUrl || AssetUrls.profile.defaultAvatar;
  }
  return url;
};

type SortColumn = 'name' | 'value' | 'youOwn';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  column: SortColumn;
  direction: SortDirection;
}

export interface ProcessedPackage {
  pkg: TradePackage;
  valuedCards: ApiTradeCard[];
  packageKey: string;
  isPackageRemoved: boolean;
  isAutoMatchActive: boolean;
  isEmptyDueToAutoMatch: boolean;
  matchingCardCount: number;
  totalCardCount: number;
  haveMatchMap?: Map<string, { haveCardId: string; condition?: string; foil?: boolean }>;
}

interface SendListViewProps {
  packages: ProcessedPackage[];
  selectedCardsPerPackage: Map<string, Set<string>>;
  removedPackages: Set<string>;
  onToggleAllCardInstances: (packageUserId: number, cardId: string, quantity: number) => void;
  onSendPackage: (pkg: TradePackage) => void;
  onRemoveAll: (userId: number) => void;
  onReset: (userId: number) => void;
  onAutoMatchToggle: (userId: number, enabled: boolean) => void;
  globalAutoMatch: boolean;
  onCardMouseEnter: (e: React.MouseEvent<HTMLElement>, card: ApiTradeCard, username: string) => void;
  onCardMouseLeave: () => void;
  haveListQuantityMap: Map<string, number>;
}

const MOBILE_BREAKPOINT = 768;

const SET_ICON_BASE = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons';

export default function SendListView({
  packages,
  selectedCardsPerPackage,
  removedPackages,
  onToggleAllCardInstances,
  onSendPackage,
  onRemoveAll,
  onReset,
  onAutoMatchToggle,
  globalAutoMatch,
  onCardMouseEnter,
  onCardMouseLeave,
  haveListQuantityMap,
}: SendListViewProps) {
  const { isMobile } = useWindowSize(MOBILE_BREAKPOINT);

  // Sort config — applies within each trader group
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'value',
    direction: 'desc',
  });

  // Expanded sets per card
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Hover state for expanded set item tooltip
  const [hoveredSetDetail, setHoveredSetDetail] = useState<{
    detail: SetDetail;
    position: { x: number; y: number };
    cardName: string;
  } | null>(null);
  const setHoverShowRef = useRef<NodeJS.Timeout | null>(null);
  const setHoverHideRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup hover timeouts on unmount
  useEffect(() => {
    return () => {
      if (setHoverShowRef.current) clearTimeout(setHoverShowRef.current);
      if (setHoverHideRef.current) clearTimeout(setHoverHideRef.current);
    };
  }, []);

  const handleSetItemMouseEnter = useCallback((e: React.MouseEvent, detail: SetDetail, cardName: string) => {
    if (isMobile) return;
    if (setHoverHideRef.current) {
      clearTimeout(setHoverHideRef.current);
      setHoverHideRef.current = null;
    }
    const pos = { x: e.clientX, y: e.clientY };
    setHoverShowRef.current = setTimeout(() => {
      setHoveredSetDetail({ detail, position: pos, cardName });
    }, 50);
  }, [isMobile]);

  const handleSetItemMouseMove = useCallback((e: React.MouseEvent) => {
    if (isMobile || !hoveredSetDetail) return;
    setHoveredSetDetail(prev => prev ? { ...prev, position: { x: e.clientX, y: e.clientY } } : null);
  }, [isMobile, hoveredSetDetail]);

  const handleSetItemMouseLeave = useCallback(() => {
    if (isMobile) return;
    if (setHoverShowRef.current) {
      clearTimeout(setHoverShowRef.current);
      setHoverShowRef.current = null;
    }
    setHoverHideRef.current = setTimeout(() => {
      setHoveredSetDetail(null);
    }, 300);
  }, [isMobile]);

  const handleSetTooltipMouseEnter = useCallback(() => {
    if (setHoverHideRef.current) {
      clearTimeout(setHoverHideRef.current);
      setHoverHideRef.current = null;
    }
  }, []);

  const handleSetTooltipMouseLeave = useCallback(() => {
    setHoverHideRef.current = setTimeout(() => {
      setHoveredSetDetail(null);
    }, 300);
  }, []);

  const handleSortClick = useCallback((column: SortColumn) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const toggleExpand = useCallback((cardId: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }, []);

  // Sort cards within a group
  const sortCards = useCallback((cards: ApiTradeCard[]): ApiTradeCard[] => {
    return [...cards].sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      if (sortConfig.column === 'name') {
        return dir * a.name.localeCompare(b.name);
      }
      if (sortConfig.column === 'youOwn') {
        const aOwn = haveListQuantityMap.get(a.name) ?? 0;
        const bOwn = haveListQuantityMap.get(b.name) ?? 0;
        return dir * (aOwn - bOwn);
      }
      // value
      return dir * (getCardDisplayPrice(a) - getCardDisplayPrice(b));
    });
  }, [sortConfig, haveListQuantityMap]);

  // Check if a card is selected (all instances)
  const isCardSelected = useCallback((packageKey: string, card: ApiTradeCard, isPackageRemoved: boolean): boolean => {
    if (isPackageRemoved) return false;
    const selectedCards = selectedCardsPerPackage.get(packageKey);
    if (selectedCards === undefined) return true; // Default: all selected
    // Check if any instance is selected
    for (let i = 0; i < card.quantity; i++) {
      if (selectedCards.has(`${card.id}-${i}`)) return true;
    }
    return false;
  }, [selectedCardsPerPackage]);

  // Count selected instances of a card
  const getSelectedCount = useCallback((packageKey: string, card: ApiTradeCard, isPackageRemoved: boolean): number => {
    if (isPackageRemoved) return 0;
    const selectedCards = selectedCardsPerPackage.get(packageKey);
    if (selectedCards === undefined) return card.quantity; // Default: all selected
    let count = 0;
    for (let i = 0; i < card.quantity; i++) {
      if (selectedCards.has(`${card.id}-${i}`)) count++;
    }
    return count;
  }, [selectedCardsPerPackage]);

  // Check if all cards in a package are selected
  const isAllSelected = useCallback((packageKey: string, cards: ApiTradeCard[], isPackageRemoved: boolean): boolean => {
    if (isPackageRemoved) return false;
    const selectedCards = selectedCardsPerPackage.get(packageKey);
    if (selectedCards === undefined) return true; // Default: all selected
    for (const card of cards) {
      for (let i = 0; i < card.quantity; i++) {
        if (!selectedCards.has(`${card.id}-${i}`)) return false;
      }
    }
    return true;
  }, [selectedCardsPerPackage]);

  // Toggle all cards in a package
  const handleToggleAll = useCallback((pkg: ProcessedPackage) => {
    const allSelected = isAllSelected(pkg.packageKey, pkg.valuedCards, pkg.isPackageRemoved);
    if (allSelected) {
      onRemoveAll(pkg.pkg.userId);
    } else {
      onReset(pkg.pkg.userId);
    }
  }, [isAllSelected, onRemoveAll, onReset]);

  // Mobile selection bar data
  const selectionSummary = useMemo(() => {
    if (!isMobile) return null;
    const traderSelections: Array<{ pkg: TradePackage; cardCount: number; totalValue: number }> = [];
    for (const processed of packages) {
      let count = 0;
      let value = 0;
      for (const card of processed.valuedCards) {
        const selected = getSelectedCount(processed.packageKey, card, processed.isPackageRemoved);
        if (selected > 0) {
          count += selected;
          value += getCardDisplayPrice(card) * selected;
        }
      }
      if (count > 0) {
        traderSelections.push({ pkg: processed.pkg, cardCount: count, totalValue: value });
      }
    }
    return traderSelections.length > 0 ? traderSelections : null;
  }, [isMobile, packages, getSelectedCount]);

  // Row click handler — toggle checkbox unless clicking an interactive element
  const handleRowClick = useCallback((e: React.MouseEvent, pkg: TradePackage, card: ApiTradeCard) => {
    const target = e.target as HTMLElement;
    // Walk up from the click target to see if we hit an interactive element
    let el: HTMLElement | null = target;
    while (el && el !== e.currentTarget) {
      const tag = el.tagName;
      if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'LABEL') return;
      if (el.classList.contains('list-sets-expandable')) return;
      if (el.classList.contains('list-expanded-set-item')) return;
      el = el.parentElement;
    }
    onToggleAllCardInstances(pkg.userId, card.id, card.quantity);
  }, [onToggleAllCardInstances]);

  const getSortArrow = (column: SortColumn) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Render mobile sort dropdown
  const renderMobileSortControls = () => {
    if (!isMobile) return null;
    return (
      <div className="list-view-controls">
        <div className="list-view-filter-group">
          <label className="list-view-filter-label">Sort by:</label>
          <select
            className="send-dropdown list-view-sort-select"
            value={`${sortConfig.column}-${sortConfig.direction}`}
            onChange={(e) => {
              const [col, dir] = e.target.value.split('-') as [SortColumn, SortDirection];
              setSortConfig({ column: col, direction: dir });
            }}
          >
            <option value="value-desc">Value (High to Low)</option>
            <option value="value-asc">Value (Low to High)</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="youOwn-asc">You Own (Low to High)</option>
            <option value="youOwn-desc">You Own (High to Low)</option>
          </select>
        </div>
      </div>
    );
  };

  // Render expanded set details row
  const renderExpandedSets = (card: ApiTradeCard) => {
    if (!expandedCards.has(card.id)) return null;
    const details = card.acceptableSetDetails;
    if (!details || details.length === 0) return null;

    return (
      <>
        <div className="list-expanded-sets">
          {details.map((detail, idx) => (
            <div
              key={`${detail.code}-${detail.collectorNumber || idx}`}
              className="list-expanded-set-item"
              onMouseEnter={(e) => handleSetItemMouseEnter(e, detail, card.name)}
              onMouseMove={handleSetItemMouseMove}
              onMouseLeave={handleSetItemMouseLeave}
            >
              <img
                src={`${SET_ICON_BASE}/${detail.code.toLowerCase()}.svg`}
                alt={detail.code}
                className="list-expanded-set-icon"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="list-expanded-set-name">{detail.name}</span>
              <span className="list-expanded-set-code">({detail.code.toUpperCase()})</span>
              {detail.collectorNumber && (
                <span className="list-expanded-set-collector">#{detail.collectorNumber}</span>
              )}
            </div>
          ))}
        </div>
        {hoveredSetDetail && !isMobile && (
          <CardImageTooltip
            scryfallId={hoveredSetDetail.detail.scryfallId}
            imageUrl={hoveredSetDetail.detail.imageUrl}
            cardName={hoveredSetDetail.cardName}
            position={hoveredSetDetail.position}
            onMouseEnter={handleSetTooltipMouseEnter}
            onMouseLeave={handleSetTooltipMouseLeave}
          />
        )}
      </>
    );
  };

  // --- MOBILE LAYOUT ---
  if (isMobile) {
    return (
      <div className="send-list-view send-list-view--mobile">
        {renderMobileSortControls()}

        {packages.map((processed) => {
          const { pkg, valuedCards, packageKey, isPackageRemoved, isAutoMatchActive, isEmptyDueToAutoMatch, matchingCardCount, totalCardCount } = processed;

          // Hide trader group if no cards
          if (valuedCards.length === 0 && !isEmptyDueToAutoMatch) return null;

          const sorted = sortCards(valuedCards);
          const allSelected = isAllSelected(packageKey, valuedCards, isPackageRemoved);
          const totalValue = calculateTotalValue(valuedCards);
          const cardCount = valuedCards.reduce((sum, c) => sum + c.quantity, 0);

          return (
            <div key={pkg.userId} className="list-trader-group--mobile">
              {/* Trader header */}
              <div className="list-trader-header--mobile" onClick={(e) => {
                // Don't navigate on checkbox click
                if ((e.target as HTMLElement).tagName === 'INPUT') return;
              }}>
                <div className="list-trader-header-row1--mobile">
                  <label className="list-trader-checkbox--mobile" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => handleToggleAll(processed)}
                    />
                  </label>
                  <AvatarWithFlag
                    avatarUrl={cleanImageUrl(pkg.avatarUrl)}
                    username={pkg.username}
                    countryCode={pkg.country || pkg.shippingAddress?.country}
                    size={28}
                    isElite={pkg.isElite}
                    showAnimatedBorder={pkg.showAnimatedBorder}
                    borderColor1={pkg.borderColor1}
                    borderColor2={pkg.borderColor2}
                  />
                  <UsernameLink
                    username={pkg.username}
                    isElite={pkg.isElite}
                    className="list-trader-username--mobile"
                  />
                  <div className="list-trader-balance--mobile">
                    <Points points={pkg.pointBalance} type="small" />
                  </div>
                </div>
                <div className="list-trader-header-row2--mobile">
                  <span className="list-trader-meta--mobile">
                    {cardCount} cards
                    <span className="list-trader-meta-sep">&#183;</span>
                    {new Intl.NumberFormat('en-US').format(totalValue)} pts total
                  </span>
                  {isAutoMatchActive && (
                    <span className="list-trader-match-badge--mobile">
                      &#9889; {matchingCardCount}/{totalCardCount}
                    </span>
                  )}
                </div>
              </div>

              {/* Empty auto-match state */}
              {isEmptyDueToAutoMatch ? (
                <div className="list-auto-match-empty">
                  <p>No cards match this trader's wants</p>
                  <button
                    className="list-auto-match-view-all"
                    onClick={() => onAutoMatchToggle(pkg.userId, false)}
                    disabled={globalAutoMatch}
                  >
                    {globalAutoMatch ? 'Turn off global Auto-Match to view all' : 'View all cards'}
                  </button>
                </div>
              ) : (
                /* Card stacked cards */
                sorted.map(card => {
                  const selected = isCardSelected(packageKey, card, isPackageRemoved);
                  const setsCount = getSetsAcceptedCount(card);
                  const isExpanded = expandedCards.has(card.id);
                  const displayPrice = getCardDisplayPrice(card);

                  return (
                    <div
                      key={(card as any)._autoMatchCompositeId || card.id}
                      className={`list-card--mobile ${!selected ? 'list-card--deselected' : ''}`}
                      onClick={(e) => handleRowClick(e, pkg, card)}
                    >
                      <div className="list-card-header--mobile">
                        <label className="list-card-checkbox--mobile">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => onToggleAllCardInstances(pkg.userId, card.id, card.quantity)}
                          />
                        </label>
                        <span className="list-card-name--mobile">
                          <a
                            className="card-link"
                            href={buildCardUrl({ name: card.name, setCode: card.setCode, scryfallId: (card as any).scryfallId })}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => onCardMouseEnter(e, card, pkg.username)}
                            onMouseLeave={onCardMouseLeave}
                          >
                            {card.name}
                          </a>
                        </span>
                        <div className="list-card-value--mobile">
                          <Points points={displayPrice} type="small" />
                        </div>
                      </div>
                      <div className="list-card-divider--mobile" />
                      <div className="list-card-details--mobile">
                        <div className="list-card-detail-row--mobile">
                          <span className="list-card-label">Sets</span>
                          <span className="list-card-value-text">{getSetsAcceptedSummary(card)}</span>
                        </div>
                        <div className="list-card-detail-row--mobile">
                          <span className="list-card-label">Finish</span>
                          <span className="list-card-value-text">{getFinishSummary(card)}</span>
                        </div>
                        <div className="list-card-detail-row--mobile">
                          <span className="list-card-label">Condition</span>
                          <span className="list-card-value-text">{getConditionSummary(card)}</span>
                        </div>
                        <div className="list-card-detail-row--mobile">
                          <span className="list-card-label">Language</span>
                          <span className="list-card-value-text">{getLanguageSummary(card)}</span>
                        </div>
                        <div className="list-card-detail-row--mobile">
                          <span className="list-card-label">Qty</span>
                          <span className="list-card-value-text">{card.quantity}</span>
                        </div>
                        {(() => {
                          const youOwnQty = haveListQuantityMap.get(card.name) ?? 0;
                          const pkgQtyForName = sorted.reduce((sum, c) => c.name === card.name ? sum + c.quantity : sum, 0);
                          const isWarning = pkgQtyForName > youOwnQty;
                          return (
                            <div className="list-card-detail-row--mobile">
                              <span className="list-card-label">You Own</span>
                              <span className={`list-card-value-text${isWarning ? ' list-card-value-text--warning' : ''}`}>{youOwnQty}</span>
                            </div>
                          );
                        })()}
                      </div>
                      {setsCount > 3 && (
                        <button
                          className="list-card-expand-btn--mobile"
                          onClick={() => toggleExpand(card.id)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? '\u25B2 Hide sets' : `\u25BC Show all ${setsCount} accepted sets`}
                        </button>
                      )}
                      {isExpanded && (
                        <div className="list-card-expanded--mobile">
                          {renderExpandedSets(card)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          );
        })}

        {/* Floating selection bar */}
        {selectionSummary && (
          <div className="list-selection-bar--mobile">
            <div className="list-selection-summary--mobile">
              {selectionSummary.reduce((sum, s) => sum + s.cardCount, 0)} cards selected
              ({selectionSummary.length} {selectionSummary.length === 1 ? 'trader' : 'traders'})
            </div>
            <div className="list-selection-traders--mobile">
              {selectionSummary.map(({ pkg, cardCount }) => (
                <div key={pkg.userId} className="list-selection-trader-row--mobile">
                  <span className="list-selection-trader-info--mobile">
                    {pkg.username} ({cardCount} {cardCount === 1 ? 'card' : 'cards'})
                  </span>
                  <button
                    className="list-selection-initiate-btn--mobile"
                    onClick={() => onSendPackage(pkg)}
                  >
                    Initiate Trade
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- DESKTOP LAYOUT ---
  return (
    <div className="send-list-view">
      <div className="send-list-table-wrapper">
        <table className="send-list-table">
          <thead>
            <tr>
              <th className="list-th-checkbox">{/* Per-trader select-all in header rows */}</th>
              <th
                className="list-th-name list-th-sortable"
                onClick={() => handleSortClick('name')}
              >
                Card Name{getSortArrow('name')}
              </th>
              <th className="list-th-sets">Sets Accepted</th>
              <th className="list-th-finish">Finish</th>
              <th className="list-th-condition">Condition</th>
              <th className="list-th-language">Language</th>
              <th className="list-th-qty">Qty</th>
              <th
                className="list-th-you-own list-th-sortable"
                onClick={() => handleSortClick('youOwn')}
              >
                You Own{getSortArrow('youOwn')}
              </th>
              <th
                className="list-th-value list-th-sortable"
                onClick={() => handleSortClick('value')}
              >
                Value{getSortArrow('value')}
              </th>
            </tr>
          </thead>

          {packages.map((processed) => {
            const { pkg, valuedCards, packageKey, isPackageRemoved, isAutoMatchActive, isEmptyDueToAutoMatch, matchingCardCount, totalCardCount } = processed;

            // Hide trader group if no cards
            if (valuedCards.length === 0 && !isEmptyDueToAutoMatch) return null;

            const sorted = sortCards(valuedCards);
            const allSelected = isAllSelected(packageKey, valuedCards, isPackageRemoved);
            const totalValue = calculateTotalValue(valuedCards);

            return (
              <tbody key={pkg.userId} className="list-trader-tbody">
                {/* Trader header row */}
                <tr className="list-trader-header-row">
                  <td colSpan={9}>
                    <div className="list-trader-header-content">
                      <div className="list-trader-header-left">
                        <label className="list-trader-checkbox" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => handleToggleAll(processed)}
                          />
                        </label>
                        <AvatarWithFlag
                          avatarUrl={cleanImageUrl(pkg.avatarUrl)}
                          username={pkg.username}
                          countryCode={pkg.country || pkg.shippingAddress?.country}
                          size={32}
                          isElite={pkg.isElite}
                          showAnimatedBorder={pkg.showAnimatedBorder}
                          borderColor1={pkg.borderColor1}
                          borderColor2={pkg.borderColor2}
                        />
                        <UsernameLink
                          username={pkg.username}
                          isElite={pkg.isElite}
                          className="list-trader-username"
                        />
                        <div className="list-trader-balance">
                          <Points points={pkg.pointBalance} type="small" />
                        </div>
                        {isAutoMatchActive && (
                          <span className="list-trader-match-badge">
                            &#9889; {matchingCardCount}/{totalCardCount}
                          </span>
                        )}
                      </div>
                      <div className="list-trader-header-right">
                        <span className="list-trader-total-label">Package Total:</span>
                        <Points points={totalValue} type="small" />
                        <div className="list-trader-auto-match">
                          <label className="list-auto-match-toggle">
                            <span className="list-auto-match-icon">&#9889;</span>
                            <input
                              type="checkbox"
                              checked={isAutoMatchActive}
                              onChange={(e) => onAutoMatchToggle(pkg.userId, e.target.checked)}
                              disabled={globalAutoMatch}
                            />
                            <span className={`list-toggle-slider ${globalAutoMatch ? 'forced-on' : ''}`} />
                          </label>
                        </div>
                        <MagicButton
                          label={isPackageRemoved ? 'Reset' : 'Remove All'}
                          icon={
                            <img
                              src={isPackageRemoved
                                ? 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/reset.svg'
                                : 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg'}
                              alt={isPackageRemoved ? 'Reset' : 'Remove'}
                              style={{ width: 14, height: 14 }}
                            />
                          }
                          color="dark-grey"
                          onPress={isPackageRemoved ? () => onReset(pkg.userId) : () => onRemoveAll(pkg.userId)}
                        />
                        <MagicButton
                          label="Send"
                          icon={
                            <img
                              src={AssetUrls.icons.misc.sendPackage}
                              alt="Send"
                              style={{ width: 16, height: 16 }}
                            />
                          }
                          color="purple"
                          onPress={() => onSendPackage({ ...pkg, cards: valuedCards })}
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Empty auto-match state */}
                {isEmptyDueToAutoMatch ? (
                  <tr className="list-empty-row">
                    <td colSpan={9}>
                      <div className="list-auto-match-empty">
                        <p>No cards from your Have List match this trader's wants</p>
                        <button
                          className="list-auto-match-view-all"
                          onClick={() => onAutoMatchToggle(pkg.userId, false)}
                          disabled={globalAutoMatch}
                        >
                          {globalAutoMatch ? 'Turn off global Auto-Match to view all' : 'View all cards'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Card rows */
                  sorted.map(card => {
                    const selected = isCardSelected(packageKey, card, isPackageRemoved);
                    const setsCount = getSetsAcceptedCount(card);
                    const isExpanded = expandedCards.has(card.id);
                    const displayPrice = getCardDisplayPrice(card);

                    return (
                      <React.Fragment key={(card as any)._autoMatchCompositeId || card.id}>
                        <tr
                          className={`list-card-row ${!selected ? 'list-card-row--deselected' : ''}`}
                          onClick={(e) => handleRowClick(e, pkg, card)}
                        >
                          <td className="list-td-checkbox">
                            <label className="list-card-checkbox">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => onToggleAllCardInstances(pkg.userId, card.id, card.quantity)}
                              />
                            </label>
                          </td>
                          <td className="list-td-name">
                            <a
                              className="card-link"
                              href={buildCardUrl({ name: card.name, setCode: card.setCode, scryfallId: (card as any).scryfallId })}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => onCardMouseEnter(e, card, pkg.username)}
                              onMouseLeave={onCardMouseLeave}
                            >
                              {card.name}
                            </a>
                          </td>
                          <td className="list-td-sets">
                            <span
                              className={`list-sets-summary ${setsCount > 3 ? 'list-sets-expandable' : ''}`}
                              onClick={setsCount > 3 ? () => toggleExpand(card.id) : undefined}
                            >
                              {getSetsAcceptedSummary(card)}
                              {setsCount > 3 && (
                                <span className="list-sets-chevron">
                                  {isExpanded ? ' \u25B2' : ' \u25BC'}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="list-td-finish">{getFinishSummary(card)}</td>
                          <td className="list-td-condition">{getConditionSummary(card)}</td>
                          <td className="list-td-language">{getLanguageSummary(card)}</td>
                          <td className="list-td-qty">{card.quantity}</td>
                          {(() => {
                            const youOwnQty = haveListQuantityMap.get(card.name) ?? 0;
                            const pkgQtyForName = sorted.reduce((sum, c) => c.name === card.name ? sum + c.quantity : sum, 0);
                            const isWarning = pkgQtyForName > youOwnQty;
                            return (
                              <td className={`list-td-you-own${isWarning ? ' list-td-you-own--warning' : ''}`}>
                                {youOwnQty}
                              </td>
                            );
                          })()}
                          <td className="list-td-value">
                            <Points points={displayPrice} type="small" />
                          </td>
                        </tr>
                        {/* Expanded sets detail row */}
                        {isExpanded && (
                          <tr className="list-expanded-row">
                            <td colSpan={9}>
                              {renderExpandedSets(card)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            );
          })}
        </table>
      </div>
    </div>
  );
}
